// transcribe.ts — ingest-time transcription (EGO-132 Phase B). Thin browser
// seam over core/'s tested pipeline: read audio.wav + task_events.jsonl +
// meta.json out of a session dir handle, provide the worker-fetch chunk
// transcriber, write transcript.json back. Same result the viewer's
// Transcribe button produces, but run automatically during Sync so a session
// arrives in R2 already transcribed.
import { transcribeSession, type RawSeg, type TranscriptV2 } from "../../core/src/transcript.ts";
import { WORKER_URL } from "./apk.ts";

const b64 = (bytes: Uint8Array): Promise<string> =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1]);
    r.onerror = () => rej(new Error("base64 encode failed"));
    r.readAsDataURL(new Blob([bytes], { type: "audio/wav" }));
  });

/**
 * Run the core pipeline over raw audio bytes and return the transcript object.
 * The one place the worker-fetch chunk transcriber lives — shared by ingest
 * (dir-handle based, below) and the review view's Re-transcribe (R2 based).
 */
export async function transcribeBuffer(
  audioBuffer: ArrayBuffer,
  opts: { taskEventsText?: string | null; audioStartNs: number; taskName?: string;
          workerUrl?: string; onProgress?: (i: number, n: number) => void },
): Promise<TranscriptV2> {
  const workerUrl = opts.workerUrl ?? WORKER_URL;
  const { transcript } = await transcribeSession(
    { audioBuffer, taskEventsText: opts.taskEventsText, audioStartNs: opts.audioStartNs, taskName: opts.taskName },
    {
      onProgress: opts.onProgress,
      transcribeChunk: async (wav, prompt, model) => {
        const resp = await fetch(workerUrl, {
          method: "POST", headers: { "content-type": "application/json" },
          body: JSON.stringify({ mode: "transcribe", audio: await b64(wav), mime: "audio/wav",
                                 ...(prompt ? { prompt } : {}), ...(model ? { model } : {}) }),
        });
        const payload = await resp.json().catch(() => null);
        if (!resp.ok) throw new Error(`worker ${resp.status}: ${JSON.stringify(payload)?.slice(0, 200)}`);
        if (!Array.isArray(payload)) throw new Error("worker returned a non-array transcript");
        return { segments: payload as RawSeg[], model: resp.headers.get("x-gemini-model") || "" };
      },
    });
  return transcript;
}

async function readText(dir: FileSystemDirectoryHandle, name: string): Promise<string | null> {
  try { return await (await (await dir.getFileHandle(name)).getFile()).text(); }
  catch { return null; }
}

export interface TranscribeResult { segments: number; windows: number; done: boolean; warnings: number }

/**
 * Transcribe the session in `dir` and write transcript.json into it. Returns
 * null (a skip, not an error) when there's nothing to transcribe: no audio, no
 * clock anchor, or a transcript already present (unless opts.force). Never
 * throws for the "no task" case — plain free-capture sessions transcribe too,
 * just without sub-task windows.
 */
export async function transcribeInto(
  dir: FileSystemDirectoryHandle,
  opts: { workerUrl?: string; force?: boolean; log?: (m: string) => void;
          onProgress?: (i: number, n: number) => void } = {},
): Promise<TranscribeResult | null> {
  const log = opts.log ?? (() => {});
  const workerUrl = opts.workerUrl ?? WORKER_URL;

  if (!opts.force) {
    const existing = await readText(dir, "transcript.json");
    if (existing) { log("  transcript.json already present — skipping STT"); return null; }
  }

  let audioBuffer: ArrayBuffer;
  try { audioBuffer = await (await (await dir.getFileHandle("audio.wav")).getFile()).arrayBuffer(); }
  catch { log("  no audio.wav — nothing to transcribe"); return null; }

  const metaText = await readText(dir, "meta.json");
  let audioStartNs: number | null = null;
  let meta: any = null;
  try { meta = metaText ? JSON.parse(metaText) : null; } catch { /* unparseable → treated as missing */ }
  // The audio clock anchor: prefer an explicit audio start, else the session
  // start. The AUDIO's true sample-0 clock is meta.audio.start_elapsed_ns
  // (nested, latency-corrected via AudioRecord.getTimestamp — see
  // docs/DATA_FLOW.md). Fall back to the session go signal only if it's absent.
  const cand = meta?.audio?.start_elapsed_ns ?? meta?.audio_start_elapsed_ns ?? meta?.start_elapsed_ns;
  if (typeof cand === "number") audioStartNs = cand;
  if (audioStartNs == null) {
    log("  meta.json has no clock anchor — a transcript couldn't be placed on the timeline; skipping");
    return null;
  }

  const taskEventsText = await readText(dir, "task_events.jsonl");
  const taskText = await readText(dir, "task.json");
  let taskName: string | undefined;
  try { taskName = taskText ? JSON.parse(taskText).name : undefined; } catch { /* ignore */ }

  const transcript = await transcribeBuffer(audioBuffer,
    { taskEventsText, audioStartNs, taskName, workerUrl, onProgress: opts.onProgress });

  const fh = await dir.getFileHandle("transcript.json", { create: true });
  const ws = await fh.createWritable();
  await ws.write(JSON.stringify(transcript, null, 2));
  await ws.close();

  const r: TranscribeResult = {
    segments: transcript.segments.length,
    windows: taskEventsText ? transcript.segments.filter((s) => typeof s.subtask === "number").length : 0,
    done: true,
    warnings: transcript.warnings?.length ?? 0,
  };
  log(`  transcript.json: ${r.segments} segments${taskEventsText ? " (task-tagged)" : ""}` +
      (r.warnings ? `, ${r.warnings} warning(s)` : ""));
  return r;
}
